import React, { useEffect, useState } from "react";
import { Colors } from "../constants/Colors";
import logo from "../assets/images/ArkilaLogo.png";
import "../styles/profileModal.css";

/**
 * Displays the signed-in admin's profile.
 * Usage: <ProfileModal isOpen={...} user={...} onClose={...} onEdit={...} />
 */
const ProfileModal = ({ isOpen, user, onClose, onEdit }) => {
  const [logoFailed, setLogoFailed] = useState(false);

  // Close on Escape, lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const initials = (user?.name || "A")
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className="profile-modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="profile-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          "--modal-gradient-start": Colors.GRADIENT_START,
          "--modal-gradient-end": Colors.GRADIENT_END,
          "--modal-text-dark": Colors.TEXT_DARK,
          "--modal-text-light": Colors.TEXT_LIGHT,
          "--modal-border": Colors.BORDER_GRAY,
          "--modal-accent": Colors.PRIMARY,
          "--modal-success": Colors.SUCCESS,
        }}
      >
        <button
          type="button"
          className="profile-modal-close"
          onClick={onClose}
          aria-label="Close profile"
        >
          <i className="bi bi-x-lg" />
        </button>

        {/* ── Banner ── */}
        <div className="profile-modal-banner">
          <div className="profile-modal-avatar-ring">
            {logoFailed ? (
              <span className="profile-modal-avatar-fallback">{initials}</span>
            ) : (
              <img
                src={logo}
                alt=""
                className="profile-modal-avatar"
                onError={() => setLogoFailed(true)}
              />
            )}
          </div>
        </div>

        {/* ── Identity ── */}
        <div className="profile-modal-body">
          <h3 id="profile-modal-title" className="profile-modal-name">
            {user?.name || "Admin"}
          </h3>
          <span className="profile-modal-role-badge">
            <i className="bi bi-patch-check-fill me-1" />
            {user?.role || "Admin"}
          </span>

          {/* ── Details ── */}
          <div className="profile-modal-details">
            <DetailRow
              icon="bi-envelope"
              label="Email"
              value={user?.email || "admin@arkila.com"}
            />
            <DetailRow
              icon="bi-calendar3"
              label="Joined"
              value={user?.joined || "July 02, 2025"}
            />
            <DetailRow
              icon="bi-shield-check"
              label="Account status"
              value="Active"
              valueClassName="profile-modal-status-active"
            />
          </div>

          {/* ── Actions ── */}
          <div className="profile-modal-actions">
            <button
              type="button"
              className="profile-modal-btn profile-modal-btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
            <button
              type="button"
              className="profile-modal-btn profile-modal-btn-primary"
              onClick={() => onEdit?.(user)}
            >
              <i className="bi bi-pencil-square me-2" />
              Edit Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ icon, label, value, valueClassName = "" }) => (
  <div className="profile-modal-row">
    <span className="profile-modal-row-icon">
      <i className={`bi ${icon}`} />
    </span>
    <span className="profile-modal-row-label">{label}</span>
    <span className={`profile-modal-row-value ${valueClassName}`}>{value}</span>
  </div>
);

export default ProfileModal;